/** Wander substate: random drift near the duty post with A* pathfinding and pauses between targets. */
import { distanceSq, worldToTile, randomAngle, angleToDirection, randomInRange, shuffle, clamp } from '../../utils/Math';
import { TILE_SIZE } from '../../utils/Constants';
import { findPath } from '../../utils/Pathfinder';
import type { Position, EnemyArchetypeConfig } from '../../utils/Types';
import {
  MovementResult, ZERO_MOVEMENT, ALL_DIRS, DUTY_RETURN_THRESHOLD, WANDER_MARGIN,
  WANDER_PAUSE_BASE_MS, WANDER_PAUSE_RANDOM_MS, WANDER_STUCK_TIMEOUT_MS, WANDER_STUCK_PROGRESS_SQ,
  tileBlocksMovement,
} from '../constants';
import type { PathHelper } from '../PathHelper';

/** Mutable wander state owned by DutySystem. */
export interface WanderState {
  target: Position | null;
  path: Position[] | null;
  pathIndex: number;
  pauseTimer: number;
  stuckTimer: number;
  lastX: number;
  lastY: number;
}

/** Create a fresh wander state. */
export function createWanderState(): WanderState {
  return { target: null, path: null, pathIndex: 0, pauseTimer: 0, stuckTimer: 0, lastX: 0, lastY: 0 };
}

/**
 * Advance the wander substate: pick random targets within wanderRadius, pathfind to them,
 * pause on arrival, then pick a new target. Returns movement and whether wander is exhausted.
 */
export function wanderMovement(
  state: WanderState,
  entityX: number, entityY: number,
  delta: number,
  dutyX: number, dutyY: number,
  config: EnemyArchetypeConfig,
  wanderDurationMs: number,
  substateTimer: { value: number },
  groundLayer: Phaser.Tilemaps.TilemapLayer,
  logs: string[],
  pathHelper: PathHelper,
): { movement: MovementResult; exhausted: boolean } {
  // Timer-based transition
  if (wanderDurationMs > 0) {
    substateTimer.value += delta;
    if (substateTimer.value >= wanderDurationMs) {
      logs.push(`DUTY wander → passive (${Math.round(substateTimer.value)}ms elapsed)`);
      substateTimer.value = 0;
      return { movement: ZERO_MOVEMENT, exhausted: true };
    }
  }
  // wanderDurationMs === -1 means wander forever

  if (config.wanderRadius <= 0) {
    return { movement: ZERO_MOVEMENT, exhausted: false };
  }

  // Pause at target before picking a new one
  if (state.pauseTimer > 0) {
    state.pauseTimer -= delta;
    return { movement: ZERO_MOVEMENT, exhausted: false };
  }

  // Pick a new target if we don't have one
  if (!state.target) {
    state.target = pickWanderTarget(dutyX, dutyY, config.wanderRadius, groundLayer);
    state.path = null;
    state.stuckTimer = 0;
    state.lastX = entityX;
    state.lastY = entityY;
    if (state.target) {
      logs.push(`DUTY wander: target (${Math.round(state.target.x)},${Math.round(state.target.y)}) from post (${Math.round(dutyX)},${Math.round(dutyY)}) radius=${config.wanderRadius}`);
    } else {
      logs.push(`DUTY wander: no walkable target found near post (${Math.round(dutyX)},${Math.round(dutyY)}) radius=${config.wanderRadius}`);
      // Pause before retrying to avoid spamming
      state.pauseTimer = WANDER_PAUSE_BASE_MS;
      return { movement: ZERO_MOVEMENT, exhausted: false };
    }
  }

  // Follow current path or compute a new one
  if (!state.path) {
    const { path: pathResult } = findPath(groundLayer, entityX, entityY, state.target.x, state.target.y);
    if (!pathResult || pathResult.length === 0) {
      // Can't reach target — discard and pause before picking a new one
      logs.push(`DUTY wander: unreachable (${Math.round(state.target.x)},${Math.round(state.target.y)})`);
      state.target = null;
      state.pauseTimer = WANDER_PAUSE_BASE_MS;
      return { movement: ZERO_MOVEMENT, exhausted: false };
    }
    state.path = pathResult;
    state.pathIndex = 0;
  }

  // Stuck detection: if the enemy hasn't moved meaningfully, abandon the target.
  // This catches enemies blocked by other enemies (colliders don't set body.blocked).
  const movedSq = distanceSq(entityX, entityY, state.lastX, state.lastY);
  if (movedSq > WANDER_STUCK_PROGRESS_SQ) {
    state.stuckTimer = 0;
    state.lastX = entityX;
    state.lastY = entityY;
  } else {
    state.stuckTimer += delta;
    if (state.stuckTimer >= WANDER_STUCK_TIMEOUT_MS) {
      logs.push(`DUTY wander: stuck for ${Math.round(state.stuckTimer)}ms at (${Math.round(entityX)},${Math.round(entityY)}) → abandon target`);
      state.target = null;
      state.path = null;
      state.stuckTimer = 0;
      state.pauseTimer = WANDER_PAUSE_BASE_MS;
      return { movement: ZERO_MOVEMENT, exhausted: false };
    }
  }

  // Follow waypoints
  const wp = pathHelper.followWaypoints(state.path, state.pathIndex, entityX, entityY, config.moveSpeed);
  state.pathIndex = wp.newIndex;

  if (wp.arrived) {
    logs.push(`DUTY wander: arrived at (${Math.round(state.target.x)},${Math.round(state.target.y)})`);
    state.target = null;
    state.path = null;
    state.stuckTimer = 0;
    state.pauseTimer = randomInRange(WANDER_PAUSE_BASE_MS, WANDER_PAUSE_BASE_MS + WANDER_PAUSE_RANDOM_MS);
    return { movement: ZERO_MOVEMENT, exhausted: false };
  }

  return {
    movement: { vx: wp.vx, vy: wp.vy, wantsAttack: false },
    exhausted: false,
  };
}

/** Pick a random walkable position near the duty post for wander drift. */
function pickWanderTarget(dutyX: number, dutyY: number, wanderRadius: number, groundLayer: Phaser.Tilemaps.TilemapLayer): Position | null {
  const maxDist = clamp(wanderRadius, 0, DUTY_RETURN_THRESHOLD - WANDER_MARGIN);
  const minDist = Math.min(TILE_SIZE * 0.5, maxDist * 0.4);

  // Phase 1: Random sampling (12 attempts)
  for (let attempt = 0; attempt < 12; attempt++) {
    const dir = angleToDirection(randomAngle());
    const dist = randomInRange(minDist, maxDist);
    const wx = dutyX + dir.x * dist;
    const wy = dutyY + dir.y * dist;
    const tile = groundLayer.getTileAt(worldToTile(wx), worldToTile(wy));
    if (!tileBlocksMovement(tile)) return { x: wx, y: wy };
  }

  // Phase 2: Systematic scan — check 8 directions at half maxDist
  const fallbackDist = maxDist * 0.5;
  const shuffled = shuffle(ALL_DIRS.slice() as { dx: number; dy: number }[]);
  for (const dir of shuffled) {
    const wx = dutyX + dir.dx * fallbackDist;
    const wy = dutyY + dir.dy * fallbackDist;
    const tile = groundLayer.getTileAt(worldToTile(wx), worldToTile(wy));
    if (!tileBlocksMovement(tile)) return { x: wx, y: wy };
  }

  return null;
}
